import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "./ui/button";
import { ErrorState } from "./States";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * The last line before a white screen. A render-time fault is a bug in this console, not
 * a customer's mistake — so the copy says so and offers the one action that reliably
 * helps (reload), rather than a retry that would re-run the same broken render.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("radar_console_render_failed", error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="mx-auto flex max-w-2xl flex-col gap-4 p-8">
        <h1 className="text-xl font-semibold text-ink">Radar hit a fault in the console</h1>
        <p className="text-sm text-ink-muted">
          This is a fault at our end, not something you did. Nothing was charged. Reloading
          usually clears it; if it keeps happening, tell INVRT what you were doing.
        </p>
        <ErrorState error={error} />
        <div>
          <Button onClick={() => window.location.reload()}>Reload Radar</Button>
        </div>
      </div>
    );
  }
}
